// ============================================================
//  RÉFÉRENTIELS — à compléter par Aliénor
//
//  Ces listes remplacent l'ancien endpoint "config" et le bloc CULTURES du
//  mock : elles ne dépendent pas de l'exploitation, donc elles vivent ici
//  plutôt que d'être retéléchargées à chaque ouverture de page.
//  Ce qui dépend de l'exploitation (localisation, région agricole, effectifs)
//  continue d'arriver par l'API de chaque graphique.
//
//  ⚠️ Squelette volontairement incomplet — complète/corrige librement.
// ============================================================

/**
 * Cultures proposées dans la sidebar.
 * `id` est la valeur envoyée à l'API (champ identifiant de culture).
 * `famille` sert à savoir quelles dates de semis proposer (cf. SEMIS_PAR_FAMILLE).
 */
export const CULTURES = [
  { id: 1184, label: "Maïs précoce",          famille: "mais" },
  { id: 1185, label: "Maïs demi-précoce C1",  famille: "mais" },
  { id: 1186, label: "Maïs demi-précoce C2",  famille: "mais" },
  { id: 1187, label: "Maïs demi-tardif",      famille: "mais" },
  { id: 1188, label: "Maïs tardif",           famille: "mais" },
  { id: 221,  label: "Tournesol",             famille: "tournesol" },
  { id: 2234, label: "Soja précocité 000",    famille: "soja" },
  { id: 2235, label: "Soja précocité 00",     famille: "soja" },
  { id: 2236, label: "Soja précocité 0",      famille: "soja" },
  // TODO Aliénor : autres cultures de l'OAD (blé, orge, colza, betterave,
  // vigne...) si elles doivent apparaître dans d'autres pages que l'eau.
];

/**
 * Dates de semis proposées selon la famille de culture.
 * `value` part à l'API au format MM.JJ ; `label` est l'affichage en clair.
 */
export const SEMIS_PAR_FAMILLE = {
  mais: [
    { value: "04.15", label: "15 avril" },
    { value: "04.20", label: "20 avril" },
    { value: "04.25", label: "25 avril" },
    { value: "05.01", label: "1er mai" },
    { value: "05.05", label: "5 mai" },
  ],
  tournesol: [
    { value: "04.05", label: "5 avril" },
    { value: "04.15", label: "15 avril" },
    { value: "04.30", label: "30 avril" },
  ],
  soja: [
    { value: "03.25", label: "25 mars" },
    { value: "04.05", label: "5 avril" },
    { value: "04.25", label: "25 avril" },
    { value: "05.10", label: "10 mai" },
  ],
};

/** Semis disponibles pour une culture donnée (par son id). */
export function getSemisPourCulture(idCulture) {
  const culture = CULTURES.find((c) => c.id === Number(idCulture));
  return culture ? SEMIS_PAR_FAMILLE[culture.famille] ?? [] : [];
}

/**
 * Types de bétail (idBetail) — niveau "atelier", utilisé par les cases à cocher
 * de la page Tension.
 * TODO Aliénor : compléter si d'autres ateliers arrivent (ovins, caprins...).
 */
export const BETAIL = [
  { id: "VL", label: "Vaches laitières" },
  { id: "C",  label: "Porc" },
];

/**
 * Catégories d'animaux (idAnimal) — niveau fin, utilisé par le graphique
 * Elevage du Tableau de bord et par le sélecteur Culture / élevage.
 * Les effectifs, eux, viennent de l'API.
 */
export const ANIMAUX = [
  { id: "VL1", idBetail: "VL", label: "Vaches en lactation" },
  { id: "VL2", idBetail: "VL", label: "Vaches taries & génisses sup 2 ans" },
  { id: "VL3", idBetail: "VL", label: "Génisses 1 à 2 ans" },
  { id: "VL4", idBetail: "VL", label: "Génisses 6 mois à 1 an" },
  { id: "C1",  idBetail: "C",  label: "Porc à l'engraissement" },
  { id: "C2",  idBetail: "C",  label: "Truie en lactation" },
  { id: "C3",  idBetail: "C",  label: "Porc post-sevrage" },
  { id: "C4",  idBetail: "C",  label: "Porc nurserie" },
];

/** Rations / alimentations (idAlim). `idBetail` restreint les choix selon
 * l'animal : Soupe/Sec ne concernent que les porcins, le reste que les
 * bovins laitiers. */
export const ALIMENTATIONS = [
  { id: 1, label: "Ensillage maïs", idBetail: "VL" },
  { id: 2, label: "Foin/paille", idBetail: "VL" },
  { id: 3, label: "Pâturage", idBetail: "VL" },
  { id: 4, label: "Ensillage maïs et pâturage", idBetail: "VL" },
  { id: 5, label: "Foin et pâturage", idBetail: "VL" },
  { id: 6, label: "Soupe", idBetail: "C" },
  { id: 7, label: "Sec", idBetail: "C" },
];

/** Alimentations proposées pour un idBetail donné ("VL" bovins, "C" porcins). */
export function alimentationsPourAnimal(idBetail) {
  return ALIMENTATIONS.filter((a) => a.idBetail === idBetail);
}

/** Conduites d'abreuvement (idAbreuv) — porcins uniquement. */
export const ABREUVEMENTS = [
  { id: 1, label: "bol" },
  { id: 2, label: "pipette" },
];

/**
 * L'abreuvement n'est demandé que pour les porcins. On se base sur le libellé
 * de l'animal ("Porc" ou "Truie") plutôt que sur l'identifiant, pour que
 * l'ajout d'une catégorie porcine fonctionne sans toucher à cette fonction.
 */
export function demandeAbreuvement(libelleAnimal = "") {
  return /porc|truie/i.test(libelleAnimal);
}

// Équivalents des constantes définies en haut du script R

export const ANNEES_ANCRAGE = [2020,2025, 2030, 2035, 2040, 2045, 2050];

export const POLITIQUES = [
  { value: "stricte_1_5ans", label: "Rare" },
  { value: "stricte_2_10ans", label: "Faible" },
  { value: "stricte_3_15ans", label: "Moyenne" },
  { value: "stricte_4_20ans", label: "Stricte" },
  { value: "stricte_5_25ans", label: "Radicale" },
];

function hexMix(hex1, hex2, t) {
  const toRgb = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
  const [r1, g1, b1] = toRgb(hex1);
  const [r2, g2, b2] = toRgb(hex2);
  const mix = (a, b) => Math.round(a + (b - a) * t);
  return `#${[mix(r1, r2), mix(g1, g2), mix(b1, b2)].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

export function getModaliteColor(modalite) {
  const m = (modalite || "").toLowerCase();
  if (m.includes("sec")) return "#cc1100";
  if (m.includes("irrig")) return "steelblue";
  const match = m.match(/(\d+)/);
  const n = match ? parseInt(match[1], 10) : 3;
  const maxN = 5;
  const t = maxN > 1 ? (n - 1) / (maxN - 1) : 0;
  return hexMix("#E6D6F2", "#3B0A5C", Math.min(1, Math.max(0, t)));
}

// ⚠️ Correspondance de libellés provisoire (à ajuster) — les valeurs à
// gauche (Mais1, Mais2...) sont celles renvoyées telles quelles par l'API
// dans le champ "modalite" ; les valeurs à droite sont juste le texte
// affiché côté front, change-les librement selon ce que ça représente
// vraiment (Mais1 = restriction la plus rare ? la plus stricte ? à confirmer).
export const LABELS_POL = {
  MaisSec: "Sec",
  MaisFullIrrig: "Irrigué 100%",
  Mais1: "Rare",
  Mais2: "Faible",
  Mais3: "Moyenne",
  Mais4: "Stricte",
  Mais5: "Radicale",
  Mais6: "Extreme",
  TournesolSec: "Sec",
  TournesolFullIrrig: "Irrigué 100%",
  Tournesol1: "Rare",
  Tournesol2: "Faible",
  Tournesol3: "Moyenne",
  Tournesol4: "Stricte",
  Tournesol5: "Radicale",
  Tournesol6: "Extreme",
};

export const ALEAS = ["CDF", "Gel", "CDC", "SHRU", "EE"];
export const ALEAS_PERTES = ["Gel", "CDC", "EE", "SHRU", "CDF", "Total"];
export const NIVEAUX_RISQUE = [
  "Tres courant",
  "Courant",
  "Exceptionnel",
  "Catastrophique",
  "Extreme",
  "Moyen",
];

// Scénario coché à l'ouverture sur toutes les pages (RCP 8.5).
export const RCP_DEFAUT = "8.5";

export const RCP_OPTIONS = [
  { value: "4.5", label: "RCP 4.5" },
  { value: "8.5", label: "RCP 8.5" },
  { value: "hybride", label: "Hybride 4.5 -> 8.5" },
  { value: "tracc", label: "TRACC" },
];

export const YEAR_COLORS = {
  2020: "#6FFF6F",
  2035: "#CB86EA",
  2050: "#FF6969",
  "reference": "#A6D96A",
  "+2": "#FDAE61",
  "+2.7": "#EA5733",
  "+4":"#CC0003"
};


// L'ordre des clés ici pilote l'ordre d'affichage partout (camemberts,
// barplots, légendes) : Gel, CDF, CDC, SHRU, EE.
export const ALEA_INFO = {
  Gel:  { label: "Gel",              color: "#70C1E6" },
  CDF:  { label: "Coup de froid",     color: "#9999FF" },
  CDC:  { label: "Coup de chaleur",   color: "#F3A147" },
  SHRU: { label: "Stress hydrique",   color: "#FFFF66" },
  EE:   { label: "Excès d'eau",       color: "#5584F9" },
};

/** Mélange générique entre deux couleurs hex (ratio 0 = hex, 1 = cible). */
export function melanger(hex, cible, ratio) {
  // Un ratio hors [0,1] fait déborder les composantes RGB et casse le hex
  // généré : toujours le borner.
  const ratioBorne = Math.min(1, Math.max(0, ratio));
  const a = parseInt(hex.replace("#", ""), 16);
  const b = parseInt(cible.replace("#", ""), 16);
  const composante = (v, decalage) => (v >> decalage) & 255;
  const m = (c1, c2) => Math.round(c1 + (c2 - c1) * ratioBorne);
  const rgb = [16, 8, 0].map((d) => m(composante(a, d), composante(b, d)));
  return `#${rgb.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Couleur MONOCHROME CONTINUE (Comparer / Fréquence des aléas) : `hexBase`
 * est la couleur de la valeur centrale (milieu de min/max, PAS min/max
 * eux-mêmes) ; min -> presque blanc, max -> très foncé. Calculée sur min/max
 * RÉELS des données affichées (les 2-3 cartes à l'écran), pas des seuils
 * fixes inventés à l'avance — sinon une échelle géographique dont les
 * valeurs sont toutes basses ou toutes hautes ne montre presque plus de
 * nuances (c'était le bug précédent).
 */
export function couleurMonochromeContinue(hexBase, min, max, valeur) {
  if (max === min) return hexBase;
  const mid = (min + max) / 2;
  if (valeur <= mid) {
    const t = (valeur - min) / (mid - min);
    return melanger("#ffffff", hexBase, t);
  }
  const t = (valeur - mid) / (max - mid);
  return melanger(hexBase, "#000000", t * 0.65);
}

/**
 * Couleur DIVERGENTE CONTINUE (Comparer / Phénologie et Eau) : blanc
 * uniquement à valeur EXACTEMENT 0, `hexPositif`/`hexNegatif` atteintes au
 * max/min réels des données affichées. Si les données affichées sont
 * toutes positives (ou toutes négatives), l'autre couleur n'est jamais
 * calculée : elle n'apparaît donc ni sur la carte ni dans la légende.
 */
export function couleurDivergenteContinue(hexPositif, hexNegatif, min, max, valeur) {
  if (valeur === 0) return "#ffffff";
  if (valeur > 0) {
    const plafond = max > 0 ? max : valeur;
    return melanger("#ffffff", hexPositif, valeur / plafond);
  }
  const plancher = min < 0 ? min : valeur;
  return melanger("#ffffff", hexNegatif, valeur / plancher);
}

/**
 * Cartes Comparer / Fréquence des aléas : la teinte suit l'aléa sélectionné
 * dans la sidebar (coup de chaleur -> tons orange, stress hydrique -> tons
 * jaune, etc.) — la couleur déjà définie pour cet aléa ailleurs dans
 * l'appli (ALEA_INFO) sert de couleur centrale au dégradé continu.
 */
export function couleurAlea(codeAlea, min, max, valeur) {
  const base = ALEA_INFO[codeAlea]?.color ?? "#999999";
  return couleurMonochromeContinue(base, min, max, valeur);
}

/**
 * Cartes Comparer / Phénologie : écart de durée de stade par rapport à
 * 2020 (jours), positif ou négatif — mais avancer ou retarder un stade
 * n'est ni bon ni mauvais en soi (contrairement à Eau, où positif = besoin
 * en hausse = rouge). Couleurs neutres : violet (positif) / sarcelle
 * (négatif), jamais rouge/vert ni rouge/bleu, ni une teinte "chaude"
 * (orange) qui suggère implicitement un sens.
 */
export const COULEURS_PHENO_ECART = { positif: "#542788", negatif: "#01665E" };

/**
 * Carte Comparer / Eau : positif = besoin en hausse = rouge, négatif =
 * besoin en baisse = bleu (cf. échange avec Aliénor sur cette page).
 */
export const COULEURS_EAU = { positif: "#B2182B", negatif: "#2166AC" };

/**
 * Carte Comparer / Risque actuel : reprise EXACTE des anciennes cartes
 * statiques (cartes_risques_PRA/RA/DEPT/REG_cour/excep/cata.js, 12 fichiers
 * dans l'ancienne appli) — même 17 paliers, vert -> jaune -> rouge, partagés
 * entre les 3 niveaux de risque (courant/exceptionnel/catastrophique) et
 * les 4 échelles géographiques : les seuils ne changent jamais, seul le
 * champ lu sur chaque feature change (risques7/20/50).
 */
export const PALETTE_RISQUE_ACTUEL = [
  { seuil: 65, couleur: "#A00003" },
  { seuil: 62, couleur: "#B72C1A" },
  { seuil: 59, couleur: "#CF5732" },
  { seuil: 56, couleur: "#E38249" },
  { seuil: 53, couleur: "#FDAE61" },
  { seuil: 50, couleur: "#FEC279" },
  { seuil: 47, couleur: "#FED791" },
  { seuil: 44, couleur: "#FDEAA7" },
  { seuil: 41, couleur: "#FFFFC0" },
  { seuil: 38, couleur: "#E7F4A9" },
  { seuil: 35, couleur: "#D3EC95" },
  { seuil: 32, couleur: "#BCE380" },
  { seuil: 29, couleur: "#A6D96A" },
  { seuil: 26, couleur: "#7DB956" },
  { seuil: 23, couleur: "#539942" },
  { seuil: 20, couleur: "#2A782E" },
  { seuil: 0, couleur: "#004E17" },
];

/**
 * Stades phénologiques "reconnaissables" (is_reco dans le mock de
 * getPheno) : ceux qu'on peut choisir dans la liste déroulante "Stade" de
 * Comparer / Phénologie. Mêmes libellés que dans le mock de getPheno
 * (mockData.js), à garder synchronisés si la liste change là-bas.
 */
export const STADES_RECO = [
  { id: "8feuilles", label: "8 feuilles" },
  { id: "floraison-femelle", label: "Floraison femelle" },
  { id: "32-humidite", label: "32% d'humidité" },
];

export const EQUIPEMENTS = [
  { value: "enrouleur", label: "Enrouleur" },
  { value: "pivot", label: "Pivot" },
  { value: "rampe", label: "Rampe" },
  { value: "gag", label: "Goutte-à-Goutte" },
];

// Paramètres par défaut par équipement (unités décrites dans l'UI)
export const DEFAULT_EQUIP_PARAMS = {
  enrouleur: {
    conso: 0.5,
    investissement: 1000,
    duree_vie: 10,
    entretien: 50.0,
    efficacite: 0.9,
    main_doeuvre: 10,
  },
  pivot: {
    conso: 0.4,
    investissement: 3000,
    duree_vie: 20,
    entretien: 120.0,
    efficacite: 0.95,
    main_doeuvre: 20,
  },
  rampe: {
    conso: 0.6,
    investissement: 1500,
    duree_vie: 12,
    entretien: 80.0,
    efficacite: 0.92,
    main_doeuvre: 15,
  },
  gag: {
    conso: 0.2,
    investissement: 4000,
    duree_vie: 15,
    entretien: 200.0,
    efficacite: 0.98,
    main_doeuvre: 30,
  },
};
/**
 * Retrouve l'identifiant d'une culture à partir de son libellé tel que le
 * renvoie page/production-rapport (qui n'expose pas les codes).
 * La comparaison ignore la casse et les accents, les libellés ne sont pas
 * toujours saisis de la même façon des deux côtés.
 */
export function trouverCultureParLibelle(libelle = "") {
  const normaliser = (texte) =>
    texte
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  const cible = normaliser(libelle);
  return CULTURES.find((c) => normaliser(c.label) === cible)
    ?? CULTURES.find((c) => cible.includes(normaliser(c.famille)));
}

/** Cultures de substitution proposées par la page Adaptation / Substitution. */
export const CULTURES_SUBSTITUTION = [
  "Blé tendre hiver", "Blé tendre printemps", "Blé dur hiver", "Blé dur printemps",
  "Orge hiver", "Orge printemps", "Maïs", "Pomme de terre de conservation",
  "Betterave", "Tournesol", "Colza", "Pois hiver", "Pois printemps",
  "Fèverole hiver", "Fèverole printemps", "Soja 0", "Soja 00", "Soja 000",
  "Lentille", "Lupin hiver", "Lupin printemps", "Pois chiches",
];

/**
 * Dates de semis par culture (liste ci-dessus), fournies par Aliénor
 * (fichier date_semis_culture.xlsx, dates converties de JJ.MM en MM.JJ).
 * Utilisé par Adaptations / Changement de semis ou de variété — en mode
 * "Semis", la culture peut être n'importe laquelle de l'assolement, pas
 * seulement maïs/soja, d'où le besoin de cette liste complète.
 */
export const SEMIS_PAR_CULTURE = {
  "Blé tendre hiver": [{ value: "10.10", label: "10 octobre" }, { value: "10.25", label: "25 octobre" }, { value: "11.01", label: "1er novembre" }, { value: "11.15", label: "15 novembre" }],
  "Blé tendre printemps": [{ value: "02.05", label: "5 février" }, { value: "03.05", label: "5 mars" }],
  "Blé dur hiver": [{ value: "10.25", label: "25 octobre" }, { value: "11.01", label: "1er novembre" }, { value: "11.10", label: "10 novembre" }],
  "Blé dur printemps": [{ value: "02.05", label: "5 février" }, { value: "03.05", label: "5 mars" }],
  "Orge hiver": [{ value: "10.01", label: "1er octobre" }, { value: "10.10", label: "10 octobre" }, { value: "10.25", label: "25 octobre" }, { value: "11.01", label: "1er novembre" }],
  "Orge printemps": [{ value: "02.05", label: "5 février" }, { value: "02.15", label: "15 février" }, { value: "03.05", label: "5 mars" }, { value: "03.20", label: "20 mars" }],
  "Maïs": [{ value: "04.15", label: "15 avril" }, { value: "04.20", label: "20 avril" }, { value: "04.25", label: "25 avril" }, { value: "05.01", label: "1er mai" }, { value: "05.05", label: "5 mai" }],
  "Pomme de terre de conservation": [{ value: "04.20", label: "20 avril" }],
  "Betterave": [{ value: "03.20", label: "20 mars" }, { value: "03.30", label: "30 mars" }, { value: "04.05", label: "5 avril" }, { value: "04.15", label: "15 avril" }],
  "Tournesol": [{ value: "04.05", label: "5 avril" }, { value: "04.15", label: "15 avril" }, { value: "04.30", label: "30 avril" }],
  "Colza": [{ value: "08.01", label: "1er août" }, { value: "08.10", label: "10 août" }, { value: "08.20", label: "20 août" }, { value: "08.30", label: "30 août" }, { value: "09.05", label: "5 septembre" }],
  "Pois hiver": [{ value: "11.01", label: "1er novembre" }, { value: "11.15", label: "15 novembre" }, { value: "11.25", label: "25 novembre" }, { value: "12.10", label: "10 décembre" }, { value: "12.25", label: "25 décembre" }],
  "Pois printemps": [{ value: "02.10", label: "10 février" }, { value: "02.20", label: "20 février" }, { value: "03.15", label: "15 mars" }],
  "Fèverole hiver": [{ value: "11.01", label: "1er novembre" }, { value: "11.15", label: "15 novembre" }, { value: "11.25", label: "25 novembre" }, { value: "12.01", label: "1er décembre" }],
  "Fèverole printemps": [{ value: "02.10", label: "10 février" }, { value: "03.15", label: "15 mars" }],
  "Soja 0": [{ value: "03.25", label: "25 mars" }, { value: "04.05", label: "5 avril" }, { value: "04.25", label: "25 avril" }, { value: "05.10", label: "10 mai" }],
  "Soja 00": [{ value: "03.25", label: "25 mars" }, { value: "04.05", label: "5 avril" }, { value: "04.25", label: "25 avril" }, { value: "05.10", label: "10 mai" }],
  "Soja 000": [{ value: "03.25", label: "25 mars" }, { value: "04.05", label: "5 avril" }, { value: "04.25", label: "25 avril" }, { value: "05.10", label: "10 mai" }],
  "Lentille": [{ value: "03.01", label: "1er mars" }, { value: "03.15", label: "15 mars" }, { value: "04.01", label: "1er avril" }],
  "Lupin hiver": [{ value: "09.20", label: "20 septembre" }, { value: "10.01", label: "1er octobre" }],
  "Lupin printemps": [{ value: "02.01", label: "1er février" }, { value: "02.15", label: "15 février" }, { value: "03.01", label: "1er mars" }, { value: "03.10", label: "10 mars" }],
  "Pois chiches": [{ value: "01.05", label: "5 janvier" }, { value: "02.01", label: "1er février" }, { value: "02.25", label: "25 février" }, { value: "03.10", label: "10 mars" }],
};

/**
 * Dates de semis pour une culture donnée par son libellé, en tolérant les
 * variantes d'écriture (même normalisation que trouverCultureParLibelle) :
 * l'assolement de l'utilisateur n'écrit pas forcément le libellé exactement
 * comme SEMIS_PAR_CULTURE ("Maïs grain" vs "Maïs"). Retombe sur la liste du
 * maïs si rien ne correspond, pour ne jamais renvoyer une liste vide.
 */
export function semisPourCultureLabel(libelle = "") {
  const normaliser = (texte) =>
    texte.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  const cible = normaliser(libelle);
  const cles = Object.keys(SEMIS_PAR_CULTURE);
  const cle =
    cles.find((c) => normaliser(c) === cible) ??
    cles.find((c) => cible.includes(normaliser(c)) || normaliser(c).includes(cible));
  return SEMIS_PAR_CULTURE[cle ?? "Maïs"];
}


/**
 * Sigles des solutions d'adaptation affichés sur le graphique du Classement
 * (le nom complet est rappelé dans le tableau de correspondance à côté).
 * Clé = nom de la solution renvoyé par l'API.
 */
export const SIGLES_SOLUTIONS = {
  "Semis Maïs": "SM",
  "Semis Tournesol": "ST",
  "Variété Maïs": "VM",
  "Substitution Maïs -> Blé tendre hiver": "SMBT",
  "Substitution Blé dur d'hiver-> Blé tendre hiver": "SBDBT",
  "Substitution Colza-> Tournesol": "SCT",
  "Substitution Blé tendre hiver -> Soja précocité 0": "SBTS",
  "Irrigation Maïs": "IM",
  "Irrigation Tournesol": "IT",
};

/**
 * Sigle d'une solution : celui de SIGLES_SOLUTIONS s'il existe, sinon les
 * initiales des mots significatifs (une solution absente de la table reste
 * ainsi lisible, sans casser le graphique).
 */
export function sigleSolution(nom) {
  if (SIGLES_SOLUTIONS[nom]) return SIGLES_SOLUTIONS[nom];
  const motsIgnores = ["de", "du", "des", "en", "d'hiver", "hiver", "->"];
  return String(nom)
    .replace(/->/g, " ")
    .split(/\s+/)
    .filter((mot) => mot && !motsIgnores.includes(mot.toLowerCase()))
    .map((mot) => mot[0].toUpperCase())
    .join("");
}
